import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react'
import { RotateCw, UserPlus, UsersRound } from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { fetchAssignmentSnapshot, STAFF_ADDED_EVENT, type AssignmentSnapshot, type StaffMember } from '../features/leads/leadCrmClient'
import { computeTeamMetrics, initialsOf } from '../features/sales-team/salesTeamMetrics'
import { AddSalespersonModal } from '../features/staff/AddSalespersonModal'
import type { Lead } from '../types/crm'
import { formatCurrency } from '../utils/format'

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: AssignmentSnapshot }

const number = new Intl.NumberFormat('en-US')

export function SalesTeamPage({ leads }: { leads: Lead[] }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const [adding, setAdding] = useState(false)
  const addTriggerRef = useRef<HTMLElement | null>(null)

  function openAdd(event: MouseEvent<HTMLButtonElement>) {
    addTriggerRef.current = event.currentTarget
    setAdding(true)
  }

  function closeAdd() {
    setAdding(false)
    window.requestAnimationFrame(() => {
      if (addTriggerRef.current?.isConnected) addTriggerRef.current.focus({ preventScroll: true })
    })
  }

  // One request: every assignment with its CRM status, plus the team.
  const load = useCallback((signal?: AbortSignal) => {
    setState({ status: 'loading' })
    fetchAssignmentSnapshot(signal).then(
      (data) => setState({ status: 'ready', data }),
      () => {
        if (!signal?.aborted) setState({ status: 'error' })
      },
    )
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  // A salesperson added here or from Quick Actions joins the list at once.
  useEffect(() => {
    function addStaff(event: Event) {
      const staff = (event as CustomEvent<StaffMember>).detail
      if (!staff?.id) return
      setState((current) =>
        current.status === 'ready' && !current.data.staff.some((member) => member.id === staff.id)
          ? { status: 'ready', data: { ...current.data, staff: [...current.data.staff, staff] } }
          : current,
      )
    }
    window.addEventListener(STAFF_ADDED_EVENT, addStaff)
    return () => window.removeEventListener(STAFF_ADDED_EVENT, addStaff)
  }, [])

  const metrics = useMemo(
    () => (state.status === 'ready' ? computeTeamMetrics(state.data.staff, state.data.assignments, leads) : null),
    [state, leads],
  )

  const addButton = (
    <button className="button button-primary team-add" onClick={openAdd} type="button">
      <UserPlus aria-hidden="true" size={16} strokeWidth={1.9} />
      Add Salesperson
    </button>
  )

  return (
    <div className="page team-page">
      <PageHeader title="Sales Team" description="Track lead ownership and closed business." action={addButton} />

      <div className="team-content">
        <dl aria-busy={state.status === 'loading'} className="team-summary">
          <div>
            <dt>Team members</dt>
            <dd>{metrics ? number.format(metrics.totals.members) : '–'}</dd>
          </div>
          <div>
            <dt>Assigned leads</dt>
            <dd>{metrics ? number.format(metrics.totals.assigned) : '–'}</dd>
          </div>
          <div>
            <dt>Won deals</dt>
            <dd>{metrics ? number.format(metrics.totals.won) : '–'}</dd>
          </div>
          <div>
            <dt>Closed value</dt>
            <dd>{metrics ? formatCurrency(metrics.totals.closedValue) : '–'}</dd>
            {metrics?.totals.wonWithoutValue ? (
              <p className="team-summary-note">
                {metrics.totals.wonWithoutValue} won {metrics.totals.wonWithoutValue === 1 ? 'deal has' : 'deals have'} no Sheet value
              </p>
            ) : null}
          </div>
        </dl>

        <section aria-labelledby="team-list-title" className="team-section">
          <div className="team-section-heading">
            <h2 id="team-list-title">Salespeople</h2>
            {metrics ? <span className="team-count">{metrics.rows.length}</span> : null}
          </div>

          <div className="team-table-frame">
            {state.status === 'error' ? (
              <div className="team-state" role="alert">
                <p>The sales team could not be loaded.</p>
                <button className="team-retry" onClick={() => load()} type="button">
                  <RotateCw aria-hidden="true" size={13} />
                  Try again
                </button>
              </div>
            ) : state.status === 'loading' ? (
              <p className="team-state team-state-quiet" role="status">Loading the sales team...</p>
            ) : metrics && metrics.rows.length === 0 ? (
              <div className="team-empty">
                <span aria-hidden="true" className="team-empty-mark">
                  <UsersRound size={20} strokeWidth={1.8} />
                </span>
                <p className="team-empty-title">No salespeople added yet.</p>
                <p className="team-empty-copy">Add your team to assign leads and track closed business.</p>
                <button className="button button-secondary" onClick={openAdd} type="button">
                  <UserPlus aria-hidden="true" size={15} strokeWidth={1.9} />
                  Add Salesperson
                </button>
              </div>
            ) : metrics ? (
              <table className="team-table">
                <thead>
                  <tr>
                    <th scope="col">Salesperson</th>
                    <th scope="col">Assigned</th>
                    <th scope="col">Active</th>
                    <th scope="col">Won</th>
                    <th scope="col">Lost</th>
                    <th scope="col">Closed value</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.rows.map((row, index) => (
                    <tr key={row.staff.id} style={{ '--row': Math.min(index, 8) } as CSSProperties}>
                      <th scope="row">
                        <span className="team-person">
                          <span aria-hidden="true" className="team-avatar">{initialsOf(row.staff.displayName)}</span>
                          <span className="team-person-copy">
                            <strong>{row.staff.displayName}</strong>
                            {row.staff.email ? <span>{row.staff.email}</span> : null}
                          </span>
                        </span>
                      </th>
                      <td data-label="Assigned">{number.format(row.assigned)}</td>
                      <td data-label="Active">{number.format(row.active)}</td>
                      <td className={row.won ? 'team-won' : undefined} data-label="Won">{number.format(row.won)}</td>
                      <td className="team-lost" data-label="Lost">{number.format(row.lost)}</td>
                      <td className="team-value" data-label="Closed">
                        {formatCurrency(row.closedValue)}
                        {row.wonWithoutValue ? (
                          <small title="Won deals whose Sheet Opportunity Value is blank or not a number">
                            +{row.wonWithoutValue} without value
                          </small>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        </section>
      </div>

      {adding ? <AddSalespersonModal onClose={closeAdd} /> : null}
    </div>
  )
}
